import * as CryptoJS from 'crypto-js';

export interface EncryptionConfig {
  algorithm: string;
  keyDerivation: {
    iterations: number;
    keySize: number;
    salt: string;
  };
}

export interface EncryptedData {
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: string;
  keyDerivation: {
    iterations: number;
    keySize: number;
  };
}

export interface SecretData {
  id: string;
  name: string;
  value: string;
  environment: 'development' | 'staging' | 'production';
  lastUsed: string;
  createdAt: string;
  updatedAt: string;
}

class EncryptionService {
  private config: EncryptionConfig;
  private masterKey: string | null = null;

  constructor() {
    this.config = {
      algorithm: 'AES-256-CBC',
      keyDerivation: {
        iterations: 100000,
        keySize: 256 / 32,
        salt: import.meta.env.VITE_ENCRYPTION_SALT || 'beehive-default-salt-change-in-production'
      }
    };
  }

  private getMasterKey(): string {
    if (this.masterKey === null) {
      const key = import.meta.env.VITE_ENCRYPTION_MASTER_KEY || 
                  'default-key-change-in-production-environment';
      
      if (key === 'default-key-change-in-production-environment' && import.meta.env.MODE === 'production') {
        throw new Error('VITE_ENCRYPTION_MASTER_KEY must be set in production environment');
      }
      
      this.masterKey = key;
    }
    
    return this.masterKey;
  }

  private deriveKey(salt: string): CryptoJS.lib.WordArray {
    return CryptoJS.PBKDF2(this.getMasterKey(), salt, {
      keySize: this.config.keyDerivation.keySize,
      iterations: this.config.keyDerivation.iterations
    });
  }

  encrypt(plaintext: string): EncryptedData {
    try {
      const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
      const iv = CryptoJS.lib.WordArray.random(128 / 8);
      const key = this.deriveKey(salt);

      const encrypted = CryptoJS.AES.encrypt(plaintext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      return {
        ciphertext: encrypted.toString(),
        iv: iv.toString(),
        salt: salt,
        algorithm: this.config.algorithm,
        keyDerivation: {
          iterations: this.config.keyDerivation.iterations,
          keySize: this.config.keyDerivation.keySize
        }
      };
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  decrypt(encryptedData: EncryptedData): string {
    try {
      const key = this.deriveKey(encryptedData.salt);
      const iv = CryptoJS.enc.Hex.parse(encryptedData.iv);

      const decrypted = CryptoJS.AES.decrypt(encryptedData.ciphertext, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!plaintext) {
        throw new Error('Decryption resulted in empty string');
      }

      return plaintext;
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  encryptSecret(secret: Omit<SecretData, 'id' | 'createdAt' | 'updatedAt'>): {
    encryptedSecret: EncryptedData;
    metadata: Omit<SecretData, 'value'>;
  } {
    const now = new Date().toISOString();
    const secretId = this.generateSecretId();
    
    const encryptedValue = this.encrypt(secret.value);
    
    const metadata: Omit<SecretData, 'value'> = {
      id: secretId,
      name: secret.name,
      environment: secret.environment,
      lastUsed: secret.lastUsed,
      createdAt: now,
      updatedAt: now
    };

    return {
      encryptedSecret: encryptedValue,
      metadata
    };
  }

  decryptSecret(encryptedData: EncryptedData, metadata: Omit<SecretData, 'value'>): SecretData {
    const decryptedValue = this.decrypt(encryptedData);
    
    return {
      ...metadata,
      value: decryptedValue
    };
  }

  private generateSecretId(): string {
    return `secret_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  validateEncryptedData(data: unknown): data is EncryptedData {
    if (typeof data !== 'object' || data === null) {
      return false;
    }
    
    const obj = data as Record<string, unknown>;
    
    return (
      typeof obj.ciphertext === 'string' &&
      typeof obj.iv === 'string' &&
      typeof obj.salt === 'string' &&
      typeof obj.algorithm === 'string' &&
      typeof obj.keyDerivation === 'object' &&
      obj.keyDerivation !== null &&
      typeof (obj.keyDerivation as Record<string, unknown>).iterations === 'number' &&
      typeof (obj.keyDerivation as Record<string, unknown>).keySize === 'number'
    );
  }

  rotateKey(oldKey: string, newKey: string, encryptedData: EncryptedData): EncryptedData {
    const oldMasterKey = this.masterKey;
    
    try {
      this.masterKey = oldKey;
      const plaintext = this.decrypt(encryptedData);
      
      this.masterKey = newKey;
      return this.encrypt(plaintext);
    } finally {
      this.masterKey = oldMasterKey;
    }
  }
}

export const encryptionService = new EncryptionService();

export default encryptionService;
